import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Pencil, Trash2, Plus, Ticket, Users, Calendar, Hash, DollarSign, Percent, ChevronDown, ChevronUp, X, Search, BarChart3 } from 'lucide-react';
import CouponAnalytics from './CouponAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const CouponsSettings = ({ clients = [], showToast }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('manage');
  const [form, setForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    applies_to: 'all',
    specific_client_ids: [],
    expires_at: '',
    is_active: true,
  });
  const [clientSearch, setClientSearch] = useState('');

  const fetchCoupons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/coupons`);
      setCoupons(res.data);
    } catch (err) {
      console.error('Error fetching coupons:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const resetForm = () => {
    setForm({
      code: '', description: '', discount_type: 'percentage', discount_value: '',
      min_order_amount: '', max_uses: '', applies_to: 'all', specific_client_ids: [],
      expires_at: '', is_active: true,
    });
    setEditing(null);
    setClientSearch('');
  };

  const openEdit = (coupon) => {
    setForm({
      code: coupon.code || '',
      description: coupon.description || '',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value?.toString() || '',
      min_order_amount: coupon.min_order_amount?.toString() || '',
      max_uses: coupon.max_uses?.toString() || '',
      applies_to: coupon.applies_to || 'all',
      specific_client_ids: coupon.specific_client_ids || [],
      expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
      is_active: coupon.is_active !== false,
    });
    setEditing(coupon.id);
    setShowForm(true);
    setClientSearch('');
  };

  const handleSave = async () => {
    if (!form.code.trim()) {
      showToast?.('Coupon code is required', 'error');
      return;
    }
    if (!form.discount_value || parseFloat(form.discount_value) <= 0) {
      showToast?.('Discount value must be greater than 0', 'error');
      return;
    }
    if (form.discount_type === 'percentage' && parseFloat(form.discount_value) > 100) {
      showToast?.('Percentage discount cannot exceed 100%', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        description: form.description.trim() || null,
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : null,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        applies_to: form.applies_to,
        specific_client_ids: form.applies_to === 'specific' ? form.specific_client_ids : [],
        expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59Z').toISOString() : null,
        is_active: form.is_active,
      };

      if (editing) {
        await axios.put(`${API}/settings/coupons/${editing}`, payload);
        showToast?.('Coupon updated', 'success');
      } else {
        await axios.post(`${API}/settings/coupons`, payload);
        showToast?.('Coupon created', 'success');
      }
      resetForm();
      setShowForm(false);
      fetchCoupons();
    } catch (err) {
      showToast?.(err.response?.data?.detail || 'Failed to save coupon', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API}/settings/coupons/${id}`);
      showToast?.('Coupon deleted', 'success');
      fetchCoupons();
    } catch (err) {
      showToast?.('Failed to delete coupon', 'error');
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/settings/coupons/${coupon.id}`, {
        is_active: !coupon.is_active,
      });
      fetchCoupons();
    } catch (err) {
      showToast?.('Failed to update coupon', 'error');
    }
  };

  const toggleClient = (clientId) => {
    setForm(prev => ({
      ...prev,
      specific_client_ids: prev.specific_client_ids.includes(clientId)
        ? prev.specific_client_ids.filter(id => id !== clientId)
        : [...prev.specific_client_ids, clientId]
    }));
  };

  const formatDiscount = (coupon) => {
    if (coupon.discount_type === 'percentage') return `${coupon.discount_value}% off`;
    return `$${coupon.discount_value.toFixed(2)} off`;
  };

  const isExpired = (coupon) => {
    if (!coupon.expires_at) return false;
    return new Date(coupon.expires_at) < new Date();
  };

  const isMaxedOut = (coupon) => {
    if (!coupon.max_uses) return false;
    return coupon.uses_count >= coupon.max_uses;
  };

  const getStatusBadge = (coupon) => {
    if (!coupon.is_active) return { label: 'Inactive', bg: '#F3F4F6', color: '#6B7280' };
    if (isExpired(coupon)) return { label: 'Expired', bg: '#FEE2E2', color: '#DC2626' };
    if (isMaxedOut(coupon)) return { label: 'Maxed Out', bg: '#FEF3C7', color: '#D97706' };
    return { label: 'Active', bg: '#D1FAE5', color: '#059669' };
  };

  const filteredClients = clients.filter(c =>
    !clientSearch || c.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredCoupons = coupons.filter(c =>
    !searchQuery ||
    c.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="coupons-settings">
      <div className="px-6 py-4 bg-gray-700 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Ticket size={20} />
          Coupons
        </h3>
        {activeSubTab === 'manage' && (
          <button
            data-testid="add-coupon-btn"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
          >
            <Plus size={16} /> Add Coupon
          </button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex border-b border-gray-200" data-testid="coupon-sub-tabs">
        <button
          data-testid="coupon-tab-manage"
          onClick={() => setActiveSubTab('manage')}
          className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeSubTab === 'manage' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <Ticket size={14} /> Manage
        </button>
        <button
          data-testid="coupon-tab-analytics"
          onClick={() => setActiveSubTab('analytics')}
          className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeSubTab === 'analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <BarChart3 size={14} /> Analytics
        </button>
      </div>

      {/* Analytics Tab */}
      {activeSubTab === 'analytics' && (
        <div className="p-6">
          <CouponAnalytics />
        </div>
      )}

      {/* Manage Tab */}
      {activeSubTab === 'manage' && (
      <div className="p-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="coupon-search"
            type="text"
            placeholder="Search coupons..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Form */}
        {showForm && (
          <div className="border border-gray-200 rounded-xl p-5 space-y-4 bg-gray-50" data-testid="coupon-form">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">{editing ? 'Edit Coupon' : 'New Coupon'}</h4>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                <input
                  data-testid="coupon-code-input"
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. SUMMER25"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  data-testid="coupon-description-input"
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Discount Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-300">
                  <button
                    data-testid="discount-type-percentage"
                    type="button"
                    onClick={() => setForm({ ...form, discount_type: 'percentage' })}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${form.discount_type === 'percentage' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    <Percent size={14} /> Percentage
                  </button>
                  <button
                    data-testid="discount-type-flat"
                    type="button"
                    onClick={() => setForm({ ...form, discount_type: 'flat' })}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${form.discount_type === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    <DollarSign size={14} /> Flat Amount
                  </button>
                </div>
              </div>

              {/* Discount Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {form.discount_type === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    {form.discount_type === 'percentage' ? '%' : '$'}
                  </span>
                  <input
                    data-testid="coupon-value-input"
                    type="number"
                    step={form.discount_type === 'percentage' ? '1' : '0.01'}
                    min="0"
                    max={form.discount_type === 'percentage' ? '100' : undefined}
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                    placeholder="0"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Min Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min. Order Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input
                    data-testid="coupon-min-order-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.min_order_amount}
                    onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                    placeholder="No minimum"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Max Uses */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usage Limit</label>
                <div className="relative">
                  <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    data-testid="coupon-max-uses-input"
                    type="number"
                    min="0"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    placeholder="Unlimited"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Expiration Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                <div className="relative">
                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    data-testid="coupon-expires-input"
                    type="date"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3 pt-5">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    data-testid="coupon-active-toggle"
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <span className="text-sm font-medium text-gray-700">Active</span>
              </div>
            </div>

            {/* Applies To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Availability</label>
              <div className="flex gap-3">
                <button
                  data-testid="coupon-applies-all"
                  type="button"
                  onClick={() => setForm({ ...form, applies_to: 'all', specific_client_ids: [] })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${form.applies_to === 'all' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  <Users size={14} /> Open to All
                </button>
                <button
                  data-testid="coupon-applies-specific"
                  type="button"
                  onClick={() => setForm({ ...form, applies_to: 'specific' })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${form.applies_to === 'specific' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  <Users size={14} /> Specific Clients
                </button>
              </div>
            </div>

            {/* Client selector */}
            {form.applies_to === 'specific' && (
              <div className="border border-gray-200 rounded-lg p-3 bg-white" data-testid="coupon-client-selector">
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded text-sm"
                  />
                </div>
                {form.specific_client_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.specific_client_ids.map(id => {
                      const cl = clients.find(c => c.id === id);
                      return cl ? (
                        <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {cl.name}
                          <button onClick={() => toggleClient(id)} className="hover:text-blue-900"><X size={12} /></button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                <div className="max-h-36 overflow-y-auto space-y-1">
                  {filteredClients.slice(0, 20).map(client => (
                    <label key={client.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={form.specific_client_ids.includes(client.id)}
                        onChange={() => toggleClient(client.id)}
                        className="w-3.5 h-3.5"
                      />
                      <span className="text-gray-800">{client.name}</span>
                      <span className="text-gray-400 text-xs ml-auto">{client.email}</span>
                    </label>
                  ))}
                  {filteredClients.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No clients found</p>}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                data-testid="coupon-cancel-btn"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                data-testid="coupon-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editing ? 'Update Coupon' : 'Create Coupon'}
              </button>
            </div>
          </div>
        )}

        {/* Coupons List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading coupons...</div>
        ) : filteredCoupons.length === 0 ? (
          <div className="text-center py-12" data-testid="coupons-empty-state">
            <Ticket size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 text-sm">{searchQuery ? 'No coupons match your search' : 'No coupons yet. Create one to get started.'}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="coupons-list">
            {filteredCoupons.map(coupon => {
              const status = getStatusBadge(coupon);
              const isOpen = expandedId === coupon.id;
              return (
                <div key={coupon.id} className="border border-gray-200 rounded-lg overflow-hidden transition-all" data-testid={`coupon-row-${coupon.id}`}>
                  <div
                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedId(isOpen ? null : coupon.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600 flex-shrink-0">
                        <Ticket size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-sm text-gray-900">{coupon.code}</span>
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: status.bg, color: status.color }}
                          >
                            {status.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{coupon.description || formatDiscount(coupon)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-sm font-semibold text-gray-900 hidden sm:block">{formatDiscount(coupon)}</span>
                      {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-3 text-sm">
                        <div>
                          <span className="text-gray-500 text-xs">Type</span>
                          <p className="font-medium text-gray-900 capitalize">{coupon.discount_type}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs">Value</span>
                          <p className="font-medium text-gray-900">{formatDiscount(coupon)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs">Uses</span>
                          <p className="font-medium text-gray-900">{coupon.uses_count}{coupon.max_uses ? ` / ${coupon.max_uses}` : ' / Unlimited'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-xs">Expires</span>
                          <p className="font-medium text-gray-900">
                            {coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : 'Never'}
                          </p>
                        </div>
                        {coupon.min_order_amount > 0 && (
                          <div>
                            <span className="text-gray-500 text-xs">Min. Order</span>
                            <p className="font-medium text-gray-900">${coupon.min_order_amount.toFixed(2)}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500 text-xs">Availability</span>
                          <p className="font-medium text-gray-900 capitalize">
                            {coupon.applies_to === 'all' ? 'Open to All' : `${coupon.specific_client_ids?.length || 0} Client(s)`}
                          </p>
                        </div>
                      </div>

                      {coupon.applies_to === 'specific' && coupon.specific_client_ids?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pb-3">
                          {coupon.specific_client_ids.map(id => {
                            const cl = clients.find(c => c.id === id);
                            return (
                              <span key={id} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                {cl?.name || id}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                        <button
                          data-testid={`coupon-toggle-${coupon.id}`}
                          onClick={(e) => { e.stopPropagation(); toggleActive(coupon); }}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${coupon.is_active ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                        >
                          {coupon.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          data-testid={`coupon-edit-${coupon.id}`}
                          onClick={(e) => { e.stopPropagation(); openEdit(coupon); }}
                          className="px-3 py-1.5 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center gap-1"
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          data-testid={`coupon-delete-${coupon.id}`}
                          onClick={(e) => { e.stopPropagation(); handleDelete(coupon.id); }}
                          className="px-3 py-1.5 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default CouponsSettings;
